import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { mockData } from '../data/mock';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

const Pricing = ({ onBookingClick }) => {
  const { t } = useThemeLanguage();

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 dark:text-white mb-6">
            {t('pricing.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {mockData.pricing.map((price) => (
            <Card 
              key={price.id}
              className="group bg-white dark:bg-gray-800 border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 flex flex-col h-full"
            >
              <CardContent className="p-6 md:p-8 text-center relative overflow-hidden flex flex-col flex-grow">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-gray-100 dark:from-gray-700 to-transparent rounded-bl-full opacity-50"></div>
                
                {/* Service Name - fixed height */}
                <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-white mb-3 md:mb-4 relative z-10 min-h-[2.5rem] md:min-h-[3rem] flex items-center justify-center">
                  {price.service}
                </h3>

                {/* Price */}
                <div className="mb-3 md:mb-4 relative z-10">
                  <div className="text-2xl md:text-3xl font-light text-black dark:text-white mb-1">
                    {price.price}
                  </div>
                  <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    {price.duration}
                  </div>
                </div>

                {/* Description - fixed height */}
                <p className="text-gray-600 dark:text-gray-400 text-xs mb-4 md:mb-6 leading-relaxed relative z-10 min-h-[2rem]">
                  {t('pricing.priceDesc')}
                </p>
                
                {/* Toll info - fixed height */}
                <div className="h-6 mb-4 md:mb-6 flex items-center justify-center">
                  {price.tollIncluded && (
                    <div className="flex items-center justify-center text-green-600 dark:text-green-400 text-xs md:text-sm">
                      <svg
                        className="w-4 h-4 mr-1 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{t('pricing.tollIncluded')}</span>
                    </div>
                  )}
                </div>

                {/* Spacer to push button to bottom */}
                <div className="flex-grow"></div>

                {/* CTA Button - always at bottom */}
                <Button 
                  onClick={onBookingClick}
                  variant="outline"
                  className="w-full border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 transition-all duration-300 group-hover:border-gray-900 dark:group-hover:border-white mt-auto"
                >
                  {t('pricing.book')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 grid md:grid-cols-3 gap-8 text-center">
          <div className="p-6">
            <div className="w-12 h-12 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white dark:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('pricing.fixedPrices')}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">{t('pricing.fixedPricesDesc')}</p>
          </div>

          <div className="p-6">
            <div className="w-12 h-12 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white dark:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('pricing.allInclusive')}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">{t('pricing.allInclusiveDesc')}</p>
          </div>

          <div className="p-6">
            <div className="w-12 h-12 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white dark:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('pricing.flexiblePayment')}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">{t('pricing.flexiblePaymentDesc')}</p>
          </div>
        </div>

        {/* Contact for custom quote */}
        <div className="mt-16 text-center bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg">
          <h3 className="text-2xl font-light text-gray-900 dark:text-white mb-4">
            {t('pricing.customQuote')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
            {t('pricing.customQuoteDesc')}
          </p>
          <a 
            href="https://wa.me/33749777371?text=Bonjour%2C%20je%20souhaiterais%20obtenir%20un%20devis%20personnalis%C3%A9%20pour%20un%20trajet.%20Merci%20!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-900 dark:border-white text-gray-900 dark:text-white hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 px-8 py-3 text-sm font-medium transition-colors"
          >
            <span>{t('pricing.requestQuote')}</span>
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
